// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { customElement, property } from 'lit/decorators.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import {
  dispatchAttributeChangedEventTypesafe,
  event,
  ShuiLitElement,
} from '../../utils/event-decorator.js';
import {
  ClickedPageMoveEvent,
  NextPageMoveEvent,
  PreviousPageMoveEvent,
} from '../../utils/events.js';
import { extendInteractiveArea } from '../../utils/interactive-area.js';
import { keyboardInteraction } from '../../utils/keyboardInteraction.js';
import { KEYCODE } from '../../utils/keycode.js';
import paginationStyles from './sh-pagination.lit.scss.js';
export const pagesListOfProperties = ['pages', 'currentPage'];
/**
 * @fires clicked-page-move {ClickedPageMoveEvent} - Emitted when clicking on a page number.
 * @fires previous-page-move {PreviousPageMoveEvent} - Emitted when clicking on the "previous" arrow.
 * @fires next-page-move {NextPageMoveEvent} - Emitted when clicking on the "next" arrow.
 * @fires pages-changed {PagesPropertyChangedEvent} - *hide Emitted when the pages property changes.
 * @fires current-page-changed {CurrentPagePropertyChangedEvent} - *hide Emitted when the current page property changes.
 */
let SHPagination = class SHPagination extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /** The role of the element. */
    /** @ignore */
    this.role = 'navigation';
    /**Default 1. Defines which page is currently active. */
    this.currentPage = 1;
  }
  static get styles() {
    return [extendInteractiveArea(), paginationStyles];
  }
  render() {
    return html`
      <ul id="ul">
        <li
          @click="${this.prevPage}"
          class="prevPageIcon ${this.currentPage <= 1 ? 'disabled' : ''}"
          id="leftArrowWrapper"
        >
          <sh-icon
            aria-label="${ifDefined(this.accessiblePreviousLabel || 'Previous')}"
            icon="arrow-line-left-s"
            size="m"
            button
            role="button"
            ?disabled=${this.currentPage <= 1}
          ></sh-icon>
        </li>
        ${this.getPaginationItems()}
        <li
          @click="${this.nextPage}"
          class="nextPageIcon ${this.currentPage >= this.pages ? 'disabled' : ''}"
          id="rightArrowWrapper"
        >
          <sh-icon
            aria-label="${ifDefined(this.accessibleNextLabel || 'Next')}"
            icon="arrow-line-right-s"
            size="m"
            button
            role="button"
            ?disabled=${this.currentPage >= this.pages}
          ></sh-icon>
        </li>
      </ul>
      <div aria-live="polite" aria-atomic="true" class="pagination-accessibility-label"></div>
    `;
  }
  getPaginationItems() {
    const paginationValues = this.getPaginationValues();
    const paginationItems = [];
    for (let item = 0; item < paginationValues.length; item++) {
      paginationItems.push(
        html` <li id="item">
          <button
            class="sh-pagination-item"
            aria-label="${ifDefined(this.accessiblePageLabel || 'Page')} ${paginationValues[item]}"
            @click="${this.handleClick}"
            ?active=${paginationValues[item] === this.currentPage}
            aria-current=${ifDefined(
              paginationValues[item] === this.currentPage ? 'page' : undefined
            )}
            ?click-disabled=${paginationValues[item].toString() === '...'}
            tabindex="${paginationValues[item].toString() === '...' ? '-1' : '0'}"
            @keyup="${(e) => {
              if (
                keyboardInteraction(e, KEYCODE.SPACE) ||
                keyboardInteraction(e, KEYCODE.ENTER) ||
                e.code === 'NumpadEnter'
              ) {
                const target = e.target;
                target.click();
                this.focusCurrent();
              }
            }}"
          >
            ${paginationValues[item]}
          </button>
        </li>`
      );
    }
    return paginationItems;
  }
  focusCurrent() {
    this.updateComplete.then(() => {
      var _a;
      const paginationItems =
        (_a = this.shadowRoot) === null || _a === void 0
          ? void 0
          : _a.querySelectorAll('.sh-pagination-item');
      const currentItem = Array.from(paginationItems).find(
        (x) => x.innerText === this.currentPage.toString()
      );
      currentItem === null || currentItem === void 0 ? void 0 : currentItem.focus();
    });
  }
  firstUpdated() {
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
  }
  update(changedProperties) {
    var _a;
    super.update(changedProperties);
    if (changedProperties.has('currentPage') || changedProperties.has('pages')) {
      this.currentPage = !this.currentPage ? 1 : this.currentPage;
      const liveRegion =
        (_a = this.shadowRoot) === null || _a === void 0
          ? void 0
          : _a.querySelector('.pagination-accessibility-label');
      if (liveRegion) {
        liveRegion.textContent = String(
          `${String(ifDefined(this.accessiblePageLabel || 'Page'))} ${this.currentPage} ${String(ifDefined(this.accessiblePageOfLabel || 'of'))} ${this.pages}`
        );
      }
    }
  }
  updated(changedProperties) {
    if (changedProperties.has('pages')) {
      this.style.display = this.pages <= 1 ? 'none' : 'flex';
    }
    dispatchAttributeChangedEventTypesafe(this, changedProperties, pagesListOfProperties);
  }
  getPaginationValues() {
    this.pages = !this.pages ? 1 : this.pages;
    let paginationValues = [];
    if (this.pages <= 7) {
      paginationValues = this.getPaginationValuesWhenPagesLessThanSeven();
    } else {
      paginationValues = this.getPaginationValuesWhenPagesGreaterThanSeven();
    }
    return paginationValues;
  }
  getPaginationValuesWhenPagesLessThanSeven() {
    const paginationValues = [];
    for (let page = 1; page <= this.pages; page++) {
      paginationValues.push(page);
    }
    return paginationValues;
  }
  getPaginationValuesWhenPagesGreaterThanSeven() {
    const fourthLastPage = this.pages - 3;
    let paginationValues = [];
    /*If current page = 1 or 2 or 3 or 4 , totalArray = [1 2 3 4 5 ... 20]*/
    if (this.currentPage <= 4) {
      paginationValues = this.getPaginationValuesWhenCurrentPageLessthanFour();
    } else if (this.currentPage > 4 && this.currentPage < fourthLastPage) {
      /*If current page = x , totalArray = [1 ... (x-1) (x) (x+1) ... N], where N = total-pages */
      paginationValues = this.getPaginationValuesWhenCurrentPageGreaterthanFour();
    } else {
      /*If current page = 17 or 18 or 19 or 20, totalArray = [1 ... 16 17 18 19 20] */
      paginationValues = this.getPaginationValuesWhenCurrentPageGreaterthanFourthlast();
    }
    return paginationValues;
  }
  getPaginationValuesWhenCurrentPageLessthanFour() {
    const paginationValues = [];
    for (let page = 1; page <= 7; page++) {
      if (page <= 5) {
        paginationValues.push(page);
      } else if (page === 6) {
        paginationValues.push('...');
      } else {
        paginationValues.push(this.pages);
      }
    }
    return paginationValues;
  }
  getPaginationValuesWhenCurrentPageGreaterthanFour() {
    const paginationValues = [];
    for (let page = 1; page <= 5; page++) {
      if (page === 1) {
        paginationValues.push(page);
      } else if (page === 2 || page === 4) {
        paginationValues.push('...');
      } else if (page === 3) {
        for (let j = 1; j <= 3; j++) {
          switch (j) {
            case 1:
              paginationValues.push(this.currentPage - 1);
              break;
            case 2:
              paginationValues.push(this.currentPage);
              break;
            case 3:
              paginationValues.push(this.currentPage + 1);
              break;
          }
        }
      } else {
        paginationValues.push(this.pages);
      }
    }
    return paginationValues;
  }
  getPaginationValuesWhenCurrentPageGreaterthanFourthlast() {
    const paginationValues = [];
    const fourthLastPage = this.pages - 3;
    for (let page = 1; page <= 3; page++) {
      switch (page) {
        case 1:
          paginationValues.push(page);
          break;
        case 2:
          paginationValues.push('...');
          break;
        case 3:
          {
            let subValue = 1;
            for (let j = 1; j <= 5; j++) {
              paginationValues.push(fourthLastPage - subValue);
              --subValue;
            }
          }
          break;
      }
    }
    return paginationValues;
  }
  handleClick(e) {
    const target = e.target;
    const clickedItem = Number(target.innerText);
    const isNotANumber = isNaN(clickedItem);
    if (!isNotANumber) {
      this.currentPage = clickedItem;
      this.clickedPageMove.emit(new ClickedPageMoveEvent());
    }
  }
  prevPage() {
    const prevCurrentPage = this.currentPage - 1;
    if (prevCurrentPage >= 1) {
      this.currentPage = prevCurrentPage;
      this.previousPageMove.emit(new PreviousPageMoveEvent());
    }
  }
  nextPage() {
    const nextCurrentPage = this.currentPage + 1;
    if (nextCurrentPage <= this.pages) {
      this.currentPage = nextCurrentPage;
      this.nextPageMove.emit(new NextPageMoveEvent());
    }
  }
};
__decorate([event()], SHPagination.prototype, 'clickedPageMove', void 0);
__decorate([event()], SHPagination.prototype, 'previousPageMove', void 0);
__decorate([event()], SHPagination.prototype, 'nextPageMove', void 0);
__decorate([event()], SHPagination.prototype, 'pagesChangedEvent', void 0);
__decorate([event()], SHPagination.prototype, 'currentPageChangedEvent', void 0);
__decorate([property({ type: Number, reflect: true })], SHPagination.prototype, 'pages', void 0);
__decorate([property({ type: String, reflect: true })], SHPagination.prototype, 'role', void 0);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-page-label' })],
  SHPagination.prototype,
  'accessiblePageLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-pageof-label' })],
  SHPagination.prototype,
  'accessiblePageOfLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-next-label' })],
  SHPagination.prototype,
  'accessibleNextLabel',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'accessible-previous-label' })],
  SHPagination.prototype,
  'accessiblePreviousLabel',
  void 0
);
__decorate(
  [property({ type: Number, reflect: true, attribute: 'current-page' })],
  SHPagination.prototype,
  'currentPage',
  void 0
);
SHPagination = __decorate([customElement('sh-pagination')], SHPagination);
export { SHPagination };
